import { useState, useEffect, useCallback, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaSignInAlt, FaUserPlus, FaUser } from 'react-icons/fa';
import { AuthUtils } from '../../../utils/helper_functions';
import { devbackendUrl } from '../../../utils/constants';
import { RootState } from '../../../store/store';
import kokkiLogo from '../../../assets/images/kokki_logo_v3.png';
import CurrencyIcon from '../Currency/currency-icon';
import { calculateLevelProgress, type LevelProgress } from '../../../utils/progression';
import { buildLoginPath, buildRegisterPath, getAuthRedirectTargetFromLocation } from '../../../utils/auth-redirect';
import classes from './global-header.module.css';

// Pages where scroll animation should be enabled
const SCROLLABLE_PAGES = ['/', '/about', '/hops'];

// Game pages where the header should be completely hidden
const GAME_PAGES = [
    '/game-lobby/',
    '/flip-card-game/game/',
    '/flip-card-game/hop/',
    '/quiz-marathon/game/',
    '/quiz-marathon/hop/',
    '/double-down/game/',
    '/double-down/hop/',
    '/test-bank-game',
    '/test-marathon-quiz',
    '/test-flip-card-game-ui',
];

// Minimum scroll delta to trigger visibility change (prevents jitter)
const SCROLL_THRESHOLD = 15;
const API_URL = devbackendUrl.replace(/\/$/, '');

const resolveProfileImageUrl = (imageUrl?: string | null) => {
    if (!imageUrl) return null;
    if (/^https?:\/\//i.test(imageUrl) || imageUrl.startsWith('data:')) return imageUrl;
    return `${API_URL}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`;
};

const GlobalHeader: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const isAuthenticated = AuthUtils.getAuthToken() && AuthUtils.isTokenValid(AuthUtils.getAuthToken()!);

    // Determine if we're on a game page (moves before all hooks to keep hook order stable)
    const isGamePage = GAME_PAGES.some(page => location.pathname.startsWith(page));

    const [isVisible, setIsVisible] = useState(true);
    const [palmsBalance, setPalmsBalance] = useState<number | null>(null);
    const [levelProgress, setLevelProgress] = useState<LevelProgress>(() => calculateLevelProgress(0));
    const [fetchedProfileImageUrl, setFetchedProfileImageUrl] = useState<string | null>(null);
    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    // Check if current page should have scroll animation
    const isScrollablePage = SCROLLABLE_PAGES.some(page =>
        page === '/' ? location.pathname === '/' : location.pathname.startsWith(page)
    );

    const updateHeader = useCallback(() => {
        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - lastScrollY.current;

        if (!isScrollablePage) {
            setIsVisible(true);
            lastScrollY.current = currentScrollY;
            ticking.current = false;
            return;
        }

        // Always show header when at top
        if (currentScrollY <= 10) {
            setIsVisible(true);
            lastScrollY.current = currentScrollY;
            ticking.current = false;
            return;
        }

        // Only trigger if scroll delta exceeds threshold (prevents jitter)
        if (Math.abs(scrollDelta) > SCROLL_THRESHOLD) {
            if (scrollDelta < 0) {
                // Scrolling up - show header
                setIsVisible(true);
            } else {
                // Scrolling down - hide header
                setIsVisible(false);
            }
            lastScrollY.current = currentScrollY;
        }

        ticking.current = false;
    }, [isScrollablePage]);

    const handleScroll = useCallback(() => {
        if (!ticking.current) {
            requestAnimationFrame(updateHeader);
            ticking.current = true;
        }
    }, [updateHeader]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Reset visibility when navigating to a new page
    useEffect(() => {
        setIsVisible(true);
        lastScrollY.current = 0;
    }, [location.pathname]);

    useEffect(() => {
        if (!isAuthenticated) {
            setPalmsBalance(null);
            setLevelProgress(calculateLevelProgress(0));
            setFetchedProfileImageUrl(null);
            return;
        }

        const token = AuthUtils.getAuthToken();
        fetch(`${devbackendUrl}user/info`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                if (data) {
                    setPalmsBalance(Number(data.currency ?? 0));
                    setLevelProgress(data.levelProgress ?? calculateLevelProgress(Number(data.points ?? 0)));
                    setFetchedProfileImageUrl(data.profileImageUrl ?? null);
                }
            })
            .catch(() => {
                setPalmsBalance(null);
                setLevelProgress(calculateLevelProgress(0));
                setFetchedProfileImageUrl(null);
            });
    }, [isAuthenticated, location.pathname]);

    // Hide header entirely during active game pages (after all hooks)
    if (isGamePage) return null;

    const headerClassName = `${classes.header} ${isVisible ? classes.headerVisible : classes.headerHidden
        }`;
    const profileImageUrl = userInfo ? userInfo.profileImageUrl : fetchedProfileImageUrl;
    const resolvedProfileImageUrl = resolveProfileImageUrl(profileImageUrl);
    const authRedirectTarget = getAuthRedirectTargetFromLocation(location.pathname, location.search);

    return (
        <header className={headerClassName}>
            {/* Logo */}
            <div className={classes.logoSection} onClick={() => navigate('/')}>
                <img src={kokkiLogo} alt="Kokki Logo" className={classes.logoImage} />
            </div>

            {/* Navigation */}
            <nav className={classes.navSection}>
                {isAuthenticated ? (
                    <>
                        <button
                            className={classes.walletButton}
                            onClick={() => navigate('/profile')}
                            aria-label="Palms balance"
                            title="Palms balance"
                        >
                            <CurrencyIcon className={classes.walletIcon} />
                            <span>{palmsBalance === null ? '...' : palmsBalance.toLocaleString()}</span>
                        </button>
                        <button
                            className={classes.profileButton}
                            style={{ '--level-progress': `${Math.round(levelProgress.progressPercent * 100)}%` } as CSSProperties}
                            onClick={() => navigate('/profile')}
                            aria-label="Profile"
                            title={`Level ${levelProgress.level}`}
                        >
                            <span className={classes.profileLevelRing}>
                                {resolvedProfileImageUrl ? (
                                    <img
                                        className={classes.profileImage}
                                        src={resolvedProfileImageUrl}
                                        alt={userInfo?.nickname ? `${userInfo.nickname} profile` : 'Profile'}
                                    />
                                ) : (
                                    <FaUser className={classes.profileIcon} />
                                )}
                            </span>
                            <span className={classes.profileLevelBadge}>{levelProgress.level}</span>
                        </button>
                    </>
                ) : (
                    <div className={classes.authButtons}>
                        <button
                            className={classes.loginButton}
                            onClick={() => navigate(buildLoginPath(authRedirectTarget))}
                        >
                            <FaSignInAlt className={classes.authIcon} />
                            Login
                        </button>
                        <button
                            className={classes.registerButton}
                            onClick={() => navigate(buildRegisterPath(authRedirectTarget))}
                        >
                            <FaUserPlus className={classes.authIcon} />
                            Sign Up
                        </button>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default GlobalHeader;
